import React from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import ClientDashboard from './components/ClientDashboard';
import LeadsDashboard from './components/LeadsDashboard';

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

/**
 * TabPanel component for displaying tab content
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - The content to display
 * @param {number} props.value - Current active tab value
 * @param {number} props.index - Index of this tab panel
 * @returns {JSX.Element} The TabPanel component
 */
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box mt={3}>{children}</Box>}
    </div>
  );
};

/**
 * a11y props generator for tabs
 *
 * @param {number} index - Tab index
 * @returns {Object} Accessibility props
 */
const a11yProps = (index: number) => {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
};

/**
 * Main Dashboard component with tabbed interface
 *
 * @returns {JSX.Element} The Dashboard component
 */
const Dashboard = () => {
  const [value, setValue] = React.useState(0);

  /**
   * Handles tab change
   *
   * @param {React.SyntheticEvent} event - The event
   * @param {number} newValue - New tab value
   */
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box m={2}>
      <Typography variant="h4" mb={2} mt={2} sx={{ ml: { xs: 6, sm: 10, md: 0 } }}>
        Dashboard
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="dashboard tabs">
          <Tab label="Clientes" {...a11yProps(0)} />
          <Tab label="Leads" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <ClientDashboard />
      </TabPanel>

      <TabPanel value={value} index={1}>
        <LeadsDashboard />
      </TabPanel>
    </Box>
  );
};

export default Dashboard;
